import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, Checkbox, List, ListItem, ListItemText, ListItemIcon, Collapse, Card, CardContent, Divider, Chip
} from '@mui/material';
import { getTests, addTest, updateTest, deleteTest, getSkis, getAvailableWeatherData } from '../api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import WeatherDataForm from '../components/WeatherDataForm';
import WeatherDataDisplay from '../components/WeatherDataDisplay';
import WeatherFilter from '../components/WeatherFilter';
import { filterByWeatherConditions, hasActiveWeatherFilters } from '../utils/weatherFilterUtils';
import { useAuth } from '../AuthContext';

// Helper function to get local date in YYYY-MM-DD format (not UTC)
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for input field (YYYY-MM-DD)
// When reading from database, we need to handle the date string properly
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Otherwise, parse it and extract the date portion
  // For timezone-naive timestamps from DB, we want to preserve the date as-is
  const date = new Date(dateString);
  // Use local date methods to avoid timezone conversion issues
  return getLocalDateString(date);
};

function TestForm({ open, onClose, onSave, initial, skis, defaultTechnique }) {

  // Get default date (current date) or use initial date
  const getDefaultDate = () => {
    if (initial?.date) {
      return formatDateForInput(initial.date);
    }
    return getLocalDateString();
  };

  const [formData, setFormData] = useState({
    technique: initial?.technique || defaultTechnique || 'classic',
    name: initial?.name || '',
    location: initial?.location || '',
    date: getDefaultDate(),
    // Standardized weather fields
    snow_temperature_c: initial?.snow_temperature_c ?? '',
    air_temperature_c: initial?.air_temperature_c ?? '',
    snow_humidity_doser: initial?.snow_humidity_doser ?? '',
    air_humidity_rh: initial?.air_humidity_rh ?? '',
    clouds_sky_parts: initial?.clouds_sky_parts ?? '',
    visibility: initial?.visibility || '',
    wind: initial?.wind || '',
    precipitation: initial?.precipitation || '',
    artificial_snow: initial?.artificial_snow || '',
    natural_snow: initial?.natural_snow || '',
    grain_size: initial?.grain_size || '',
    snow_humidity: initial?.snow_humidity || '',
    track_hardness: initial?.track_hardness || '',
    track_shininess: initial?.track_shininess || '',
    track_dirtiness: initial?.track_dirtiness || '',
    notes: initial?.notes || ''
  });
  const [selectedSkis, setSelectedSkis] = useState([]);
  const [rankedSkis, setRankedSkis] = useState([]);
  const [error, setError] = useState('');
  const [showSkiSelect, setShowSkiSelect] = useState(initial ? false : true);
  const [saving, setSaving] = useState(false);
  const [availableWeatherData, setAvailableWeatherData] = useState([]);
  const [loadingWeatherData, setLoadingWeatherData] = useState(false);
  const [dateRangeDays, setDateRangeDays] = useState(7);

  useEffect(() => {
    setFormData({
      technique: initial?.technique || defaultTechnique || 'classic',
      name: initial?.name || '',
      location: initial?.location || '',
      date: initial?.date ? formatDateForInput(initial.date) : getLocalDateString(),
      // Standardized weather fields
      snow_temperature_c: initial?.snow_temperature_c ?? '',
      air_temperature_c: initial?.air_temperature_c ?? '',
      snow_humidity_doser: initial?.snow_humidity_doser ?? '',
      air_humidity_rh: initial?.air_humidity_rh ?? '',
      clouds_sky_parts: initial?.clouds_sky_parts ?? '',
      visibility: initial?.visibility || '',
      wind: initial?.wind || '',
      precipitation: initial?.precipitation || '',
      artificial_snow: initial?.artificial_snow || '',
      natural_snow: initial?.natural_snow || '',
      grain_size: initial?.grain_size || '',
      snow_humidity: initial?.snow_humidity || '',
      track_hardness: initial?.track_hardness || '',
      track_shininess: initial?.track_shininess || '',
      track_dirtiness: initial?.track_dirtiness || '',
      notes: initial?.notes || ''
    });
    if (initial?.ski_ids_ranked) {
      setSelectedSkis(initial.ski_ids_ranked);
      setRankedSkis(initial.ski_ids_ranked.map(id => skis.find(s => s.id === id)).filter(Boolean));
    } else {
      setSelectedSkis([]);
      setRankedSkis([]);
    }
    setError('');
    setShowSkiSelect(initial ? false : true);
  }, [open, initial, skis, defaultTechnique]);

  const loadAvailableWeatherData = useCallback(async () => {
    setLoadingWeatherData(true);
    try {
      const data = await getAvailableWeatherData(dateRangeDays);
      setAvailableWeatherData(data);
    } catch (err) {
      console.error('Failed to load available weather data:', err);
    } finally {
      setLoadingWeatherData(false);
    }
  }, [dateRangeDays]);

  useEffect(() => {
    if (open && !initial) {
      loadAvailableWeatherData();
    }
  }, [open, initial, loadAvailableWeatherData]);

  const handleSelectWeatherData = (entry) => {
    if (!entry) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      location: entry.location || prev.location,
      snow_temperature_c: entry.snow_temperature_c ?? '',
      air_temperature_c: entry.air_temperature_c ?? '',
      snow_humidity_doser: entry.snow_humidity_doser ?? '',
      air_humidity_rh: entry.air_humidity_rh ?? '',
      clouds_sky_parts: entry.clouds_sky_parts ?? '',
      visibility: entry.visibility || '',
      wind: entry.wind || '',
      precipitation: entry.precipitation || '',
      artificial_snow: entry.artificial_snow || '',
      natural_snow: entry.natural_snow || '',
      grain_size: entry.grain_size || '',
      snow_humidity: entry.snow_humidity || '',
      track_hardness: entry.track_hardness || '',
      track_shininess: entry.track_shininess || '',
      track_dirtiness: entry.track_dirtiness || ''
    }));
  };
  const availableSkis = skis.filter(s => s.type === formData.technique);
  const handleSkiToggle = id => {
    if (selectedSkis.includes(id)) {
      setSelectedSkis(selectedSkis.filter(sid => sid !== id));
      setRankedSkis(rankedSkis.filter(s => s.id !== id));
    } else {
      setSelectedSkis([...selectedSkis, id]);
      const ski = availableSkis.find(s => s.id === id);
      setRankedSkis([...rankedSkis, ski]);
    }
  };
  const onDragEnd = result => {
    if (!result.destination) return;
    const items = Array.from(rankedSkis);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setRankedSkis(items);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (rankedSkis.length < 2) {
      setError('Select and rank at least 2 skis.');
      return;
    }
    setSaving(true);
    try {
      // Convert date to timestamp string without timezone conversion
      // The date input provides YYYY-MM-DD, we append time to create a timezone-neutral timestamp
      // This ensures the date stored matches what the user entered, regardless of their timezone
      const dateValue = formData.date ? `${formData.date}T00:00:00` : null;
      
      await onSave({
        ...formData,
        date: dateValue,
        ski_ids_ranked: rankedSkis.map(s => s.id),
        snow_temperature_c: formData.snow_temperature_c === '' ? null : Number(formData.snow_temperature_c),
        air_temperature_c: formData.air_temperature_c === '' ? null : Number(formData.air_temperature_c),
        snow_humidity_doser: formData.snow_humidity_doser === '' ? null : Number(formData.snow_humidity_doser),
        air_humidity_rh: formData.air_humidity_rh === '' ? null : Number(formData.air_humidity_rh),
        clouds_sky_parts: formData.clouds_sky_parts === '' ? null : Number(formData.clouds_sky_parts)
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initial ? 'Edit Test' : 'Add Test'}</DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          
          <TextField label="Test Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} fullWidth />
          <TextField 
            label="Test Date" 
            type="date" 
            value={formData.date} 
            onChange={e => setFormData({...formData, date: e.target.value})} 
            InputLabelProps={{ shrink: true }}
            fullWidth 
          />
          <TextField label="Location" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} fullWidth />
          <TextField select label="Technique" value={formData.technique} onChange={e => setFormData({...formData, technique: e.target.value})} required fullWidth>
            {[{ value: 'classic', label: 'Classic' }, { value: 'skate', label: 'Skate' }].map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          {!initial && availableWeatherData.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Use Team Weather Data</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Date Range"
                  value={dateRangeDays}
                  onChange={(e) => {
                    setDateRangeDays(Number(e.target.value));
                  }}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value={3}>3 Days</MenuItem>
                  <MenuItem value={7}>1 Week</MenuItem>
                  <MenuItem value={14}>2 Weeks</MenuItem>
                  <MenuItem value={21}>3 Weeks</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Select Weather Data Entry"
                  value=""
                  onChange={(e) => {
                    const entry = availableWeatherData.find(w => w.id === Number(e.target.value));
                    handleSelectWeatherData(entry);
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: 200 }}
                  disabled={loadingWeatherData}
                >
                  <MenuItem value="">None</MenuItem>
                  {availableWeatherData.map((entry) => (
                    <MenuItem key={entry.id} value={entry.id}>
                      {entry.team_name} - {entry.location || 'No location'} ({new Date(entry.date).toLocaleDateString()})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          )}
          
          {/* Weather Data Form */}
          <WeatherDataForm
            weatherData={formData}
            onChange={setFormData}
            title="Weather Conditions"
            compact={true}
          />
          
          <TextField label="Notes" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} multiline rows={2} fullWidth />
          <Button onClick={() => setShowSkiSelect(v => !v)} sx={{ alignSelf: 'flex-start', mb: 1 }}>
            {showSkiSelect ? 'Hide Chosen Skis' : 'Edit Chosen Skis'}
          </Button>
          <Collapse in={showSkiSelect}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Select Skis to Include</Typography>
            <List dense sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
              {availableSkis.map(ski => (
                <ListItem key={ski.id} button onClick={() => handleSkiToggle(ski.id)}>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selectedSkis.includes(ski.id)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={`#${ski.ski_number} - ${ski.brand}`} />
                </ListItem>
              ))}
            </List>
          </Collapse>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Rank Skis (Drag to reorder)</Typography>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="skis">
              {(provided) => (
                <List ref={provided.innerRef} {...provided.droppableProps} sx={{ minHeight: 60, border: '1px solid #eee', borderRadius: 1 }}>
                  {rankedSkis.map((ski, idx) => (
                    <Draggable key={ski.id} draggableId={ski.id.toString()} index={idx}>
                      {(provided, snapshot) => (
                        <ListItem ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} sx={{ background: snapshot.isDragging ? '#f0f0f0' : 'inherit', mb: 1, borderRadius: 1 }}>
                          <ListItemText primary={`#${ski.ski_number} - ${ski.brand}`} secondary={`Rank: ${idx + 1}`} />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TestsTable({ tests, onEdit, onDelete, onDetail, skis }) {
  const skiMap = Object.fromEntries(skis.map(ski => [ski.id, ski.ski_number]));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {tests.map((test) => {
          const rankedSkisDisplay = test.ski_ids_ranked?.slice(0, 5).map((id, idx) => `#${skiMap[id] || id}`).join(', ') || 'None';
          const hasMore = test.ski_ids_ranked?.length > 5;
          return (
            <Card key={test.id} sx={{ boxShadow: 2 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {test.name || 'Unnamed Test'}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => onDetail(test)}><InfoIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => onEdit(test)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onDelete(test.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {test.location && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Location:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{test.location}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Date:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{test.date ? test.date.split('T')[0] : '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Technique:</Typography>
                    <Chip label={test.technique} size="small" sx={{ textTransform: 'capitalize' }} />
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Ranked Skis:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {rankedSkisDisplay}
                      {hasMore && ` (+${test.ski_ids_ranked.length - 5} more)`}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }
  
  // Desktop table view
  return (
    <TableContainer
      component={Paper}
      sx={{
        mb: 4,
        borderRadius: 2,
        boxShadow: 2,
        width: '100%',
      }}
    >
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ background: '#f5f5f5' }}>
            <TableCell>Name</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Technique</TableCell>
            <TableCell>Ranked Skis</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tests.map((test) => (
            <TableRow key={test.id} hover>
              <TableCell>{test.name || ''}</TableCell>
              <TableCell>{test.location || ''}</TableCell>
              <TableCell>{test.date ? test.date.split('T')[0] : ''}</TableCell>
              <TableCell>{test.technique}</TableCell>
              <TableCell>
                {test.ski_ids_ranked?.slice(0, 5).map((id, idx) => `#${skiMap[id] || id}${idx < Math.min(test.ski_ids_ranked.length, 5) - 1 ? ', ' : ''}`).join('')}
                {test.ski_ids_ranked?.length > 5 && ' ...'}
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={1} justifyContent="center">
                  <IconButton size="small" onClick={() => onDetail(test)}><InfoIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => onEdit(test)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => onDelete(test.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TestDetailModal({ open, onClose, test, skis }) {
  const [skiDetail, setSkiDetail] = useState(null);
  
  if (!test) return null;
  const skiMap = Object.fromEntries((skis || []).map(ski => [ski.id, ski]));
  const rankedSkis = (test.ski_ids_ranked || []).map((id, idx) => ({ rank: idx + 1, ...skiMap[id] }));
  
  // Determine column headers based on brands in the test
  const brandsInTest = [...new Set(rankedSkis.map(ski => ski.brand))];
  const hasFischer = brandsInTest.includes('Fischer');
  const hasSalomonAtomic = brandsInTest.some(brand => ['Salomon', 'Atomic'].includes(brand));
  
  let flexHeader, camberHeader;
  if (hasFischer && !hasSalomonAtomic) {
    // Only Fischer skis
    flexHeader = 'FA';
    camberHeader = 'HR';
  } else if (hasSalomonAtomic && !hasFischer) {
    // Only Salomon/Atomic skis
    flexHeader = 'MF';
    camberHeader = 'H4';
  } else {
    // Mixed brands or other brands
    flexHeader = 'Flex';
    camberHeader = 'Camber';
  }
  
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Test Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            {test.name && <Typography><b>Name:</b> {test.name}</Typography>}
            {test.location && <Typography><b>Location:</b> {test.location}</Typography>}
            <Typography><b>Date:</b> {test.date ? test.date.split('T')[0] : '-'}</Typography>
            <Typography><b>Technique:</b> {test.technique}</Typography>
            {test.notes && <Typography><b>Notes:</b> {test.notes}</Typography>}
          </Box>
          
          {/* Weather Data Display */}
          <WeatherDataDisplay
            weatherData={test}
            title="Weather Conditions"
            compact={true}
          />
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Ski #</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>{flexHeader}</TableCell>
                  <TableCell>{camberHeader}</TableCell>
                  <TableCell>Grind</TableCell>
                  <TableCell>Mold</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rankedSkis.map((ski, i) => {
                  const isFischer = ski.brand === 'Fischer';
                  const isSalomonAtomic = ['Salomon', 'Atomic'].includes(ski.brand);
                  const flexValue = isFischer ? ski.fa : (isSalomonAtomic ? ski.mf : '');
                  const camberValue = isFischer ? ski.hr : (isSalomonAtomic ? ski.h4 : '');
                  
                  return (
                    <TableRow key={ski.id || i}>
                      <TableCell>{ski.rank}</TableCell>
                      <TableCell>{ski.ski_number}</TableCell>
                      <TableCell>{ski.brand}</TableCell>
                      <TableCell>{flexValue}</TableCell>
                      <TableCell>{camberValue}</TableCell>
                      <TableCell>{ski.grind}</TableCell>
                      <TableCell>{ski.mold}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => setSkiDetail(ski)}>
                          <InfoIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Ski Detail Modal */}
      <Dialog open={!!skiDetail} onClose={() => setSkiDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ski #{skiDetail?.ski_number} Details</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1 }}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Brand</TableCell><TableCell>{skiDetail?.brand}</TableCell></TableRow>
                {skiDetail?.brand === 'Fischer' && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>FA (Flex)</TableCell><TableCell>{skiDetail?.fa}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>HR (Camber Height)</TableCell><TableCell>{skiDetail?.hr}</TableCell></TableRow>
                  </>
                )}
                {['Salomon', 'Atomic'].includes(skiDetail?.brand) && (
                  <>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>MF (Flex)</TableCell><TableCell>{skiDetail?.mf}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>H4 (Camber Height)</TableCell><TableCell>{skiDetail?.h4}</TableCell></TableRow>
                  </>
                )}
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Grind</TableCell><TableCell>{skiDetail?.grind}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Mold</TableCell><TableCell>{skiDetail?.mold}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Year Made</TableCell><TableCell>{skiDetail?.year_made}</TableCell></TableRow>
                <TableRow><TableCell sx={{ fontWeight: 600 }}>Type</TableCell><TableCell>{skiDetail?.type}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkiDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export { TestDetailModal };

export default function TestsSection() {
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [skis, setSkis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, test: null });
  const [detail, setDetail] = useState({ open: false, test: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, testId: null });
  const [weatherFilters, setWeatherFilters] = useState({});
  const { user } = useAuth();

  const fetchTestsAndSkis = async () => {
    setLoading(true);
    try {
      const [testsData, skisData] = await Promise.all([getTests(), getSkis()]);
      setTests(testsData);
      setFilteredTests(testsData);
      setSkis(skisData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTestsAndSkis(); }, []);

  // Apply weather filters whenever tests or filters change
  useEffect(() => {
    if (hasActiveWeatherFilters(weatherFilters)) {
      const filtered = filterByWeatherConditions(tests, weatherFilters);
      setFilteredTests(filtered);
    } else {
      setFilteredTests(tests);
    }
  }, [tests, weatherFilters]);

  const handleWeatherFilterChange = (filters) => {
    setWeatherFilters(filters);
  };

  const handleClearWeatherFilters = () => {
    setWeatherFilters({});
  };

  const handleAdd = () => setModal({ open: true, test: null });
  const handleEdit = test => setModal({ open: true, test });
  const handleDelete = testId => setConfirmDelete({ open: true, testId });
  const handleDetail = test => setDetail({ open: true, test });
  const confirmDeleteTest = async () => {
    await deleteTest(confirmDelete.testId);
    setConfirmDelete({ open: false, testId: null });
    fetchTestsAndSkis();
  };
  const handleSave = async (form) => {
    if (modal.test) {
      await updateTest(modal.test.id, form);
    } else {
      await addTest(form);
    }
    setModal({ open: false, test: null });
    fetchTestsAndSkis();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ mt: 2, px: isMobile ? 1 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
        <Typography variant="h6" sx={{ flex: isMobile ? '1 1 100%' : 'none' }}>Tests</Typography>
        <Button variant="contained" onClick={handleAdd} fullWidth={isMobile} size={isMobile ? 'medium' : 'medium'}>Add Test</Button>
      </Box>
      
      {/* Weather Filter */}
      <WeatherFilter 
        onFilterChange={handleWeatherFilterChange}
        onClearFilters={handleClearWeatherFilters}
        compact={false}
        title="Filter Tests by Weather Conditions"
      />
      
      {loading ? <CircularProgress /> : (
        filteredTests.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {tests.length === 0 ? 'No tests yet' : 'No tests match your weather filters'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {tests.length === 0 
                ? 'Add your first test to track your performance and improvements.'
                : 'Try adjusting your weather filters to see more tests.'
              }
            </Typography>
            <Button
              variant="contained"
              onClick={tests.length === 0 ? handleAdd : handleClearWeatherFilters}
            >
              {tests.length === 0 ? 'Add Your First Test' : 'Clear Weather Filters'}
            </Button>
          </Paper>
        ) : (
          <TestsTable tests={filteredTests} onEdit={handleEdit} onDelete={handleDelete} onDetail={handleDetail} skis={skis} />
        )
      )}
      <TestForm open={modal.open} onClose={() => setModal({ open: false, test: null })} onSave={handleSave} initial={modal.test} skis={skis} defaultTechnique={user?.default_technique} />
      <TestDetailModal open={detail.open} onClose={() => setDetail({ open: false, test: null })} test={detail.test} skis={skis} />
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, testId: null })}>
        <DialogTitle>Delete Test?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this test? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, testId: null })}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteTest} variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 